import {
  computed,
  ElementRef,
  inject,
  Injectable,
  OnDestroy
} from '@angular/core';
import { debounceTime, filter, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import Basemap from '@arcgis/core/Basemap';
import Extent from '@arcgis/core/geometry/Extent';
import Map from '@arcgis/core/Map';
import MapView from '@arcgis/core/views/MapView';
import SceneView from '@arcgis/core/views/SceneView';
import * as reactiveUtils from '@arcgis/core/core/reactiveUtils';

import { EsriViewType, ViewConfig } from '@jeppesen-foreflight/jmkw-types';
import { NGXLogger } from 'ngx-logger';
import { CONFIG_TOKEN } from '../../../../jmkw.config';
import { store } from '../../../../jmkw.store';
import { BasemapManagerService } from '../../basemaps/basemap-manager.service';
import { safeGetItem, safeSetItem } from '../../../utils/safe-local-storage';
import {
  DEFAULT_MAP_VIEW_PROPERTIES,
  DEFAULT_SCENE_VIEW_PROPERTIES
} from '../../DEFAULT_VIEW_PROPERTIES';

const STORAGE_KEY = 'lastMapExtent';
const EXTENT_DEBOUNCE_TIME = 300; // 300ms debounce time

@Injectable({
  providedIn: 'root'
})
export class ViewService implements OnDestroy {
  private readonly _env = inject(CONFIG_TOKEN);
  private readonly _basemapManager = inject(BasemapManagerService);
  private readonly _logger = inject(NGXLogger);
  private readonly _destroyed$ = new Subject<void>();
  private readonly _extentChange$ = new Subject<Extent>();

  public readonly $mapView = computed(
    () => store.views['2D'].view() as MapView
  );
  public readonly $sceneView = computed(
    () => store.views['3D'].view() as SceneView
  );

  constructor() {
    // Set up debounced extent saving
    this._extentChange$
      .pipe(
        takeUntil(this._destroyed$),
        debounceTime(EXTENT_DEBOUNCE_TIME),
        filter((extent): extent is Extent => !!extent)
      )
      .subscribe((extent) => this.saveExtent(extent));

    this._basemapManager.basemapChange$
      .pipe(
        takeUntil(this._destroyed$),
        filter((basemap: Basemap | null): basemap is Basemap => !!basemap)
      )
      .subscribe((basemap: Basemap) => {
        if (basemap) {
          this.setBasemap(basemap);
        }
      });
  }

  ngOnDestroy(): void {
    this._destroyed$.next();
    this._destroyed$.complete();
  }

  private saveExtent(extent: Extent): void {
    if (!extent) return;

    const extentData = {
      xmin: extent.xmin,
      ymin: extent.ymin,
      xmax: extent.xmax,
      ymax: extent.ymax,
      spatialReference: extent.spatialReference.wkid
    };
    // Guarded: localStorage throws in Safari private browsing and when the quota is
    // exceeded. Failing to persist the extent must not break panning (JMKW-1808).
    safeSetItem(STORAGE_KEY, JSON.stringify(extentData), (error) =>
      this._logger.debug(
        '[ViewService] localStorage unavailable, extent not persisted',
        error
      )
    );
  }

  private loadSavedExtent(): Extent | null {
    // Guarded: this runs during view initialization, so an unhandled throw here would
    // prevent the map from initializing at all (JMKW-1808).
    const savedExtent = safeGetItem(STORAGE_KEY, (error) =>
      this._logger.warn(
        '[ViewService] localStorage unavailable, using default extent',
        error
      )
    );
    if (!savedExtent) return null;

    try {
      const extentData = JSON.parse(savedExtent);
      return new Extent({
        xmin: extentData.xmin,
        ymin: extentData.ymin,
        xmax: extentData.xmax,
        ymax: extentData.ymax,
        spatialReference: { wkid: extentData.spatialReference }
      });
    } catch (error) {
      console.warn('Failed to parse saved extent:', error);
      return null;
    }
  }

  init(map: Map): Record<EsriViewType, ViewConfig> {
    const savedExtent = this._env.persistView ? this.loadSavedExtent() : null;

    const mapViewProperties = {
      ...DEFAULT_MAP_VIEW_PROPERTIES,
      ...(savedExtent && { extent: savedExtent })
    };

    const sceneViewProperties = {
      ...DEFAULT_SCENE_VIEW_PROPERTIES,
      ...(savedExtent && { extent: savedExtent })
    };

    const mapView = this.createView(map, '2D', mapViewProperties) as MapView;
    const sceneView = this.createView(
      map,
      '3D',
      sceneViewProperties
    ) as SceneView;

    store.views['2D'].view.set(mapView);
    store.views['3D'].view.set(sceneView);

    this.syncViews([mapView, sceneView]);

    // Watch for extent changes and emit to subject using reactiveUtils
    reactiveUtils.watch(
      () => mapView.extent,
      (extent) => this._extentChange$.next(extent),
      { initial: false }
    );

    return store.views.unwrap();
  }

  private createView(
    map: Map,
    viewType: EsriViewType,
    properties: any
  ): MapView | SceneView {
    const ViewClass = viewType === '2D' ? MapView : SceneView;

    const view = new ViewClass({
      ...properties,
      map,
      // ArcGIS v5: 'attribution' was removed as a ui.components string identifier.
      // Attribution is now part of the view itself, controlled via attributionVisible.
      attributionVisible: !store.env.hideAttributionBar?.(),
      ui: {
        components: [...(!store.env.hideZoomButtons?.() ? ['zoom'] : [])]
      }
    });

    view.popupEnabled = false;
    return view;
  }

  private syncViews(views: [MapView, SceneView]): void {
    let activeView: MapView | SceneView;
    let syncing = false;

    const sync = (source: MapView | SceneView) => {
      if (!syncing && activeView?.viewpoint && activeView === source) {
        syncing = true;
        views.forEach((view) => {
          if (view !== activeView) {
            view.viewpoint = activeView.viewpoint;
          }
        });
        syncing = false;
      }
    };

    views.forEach((view) => {
      reactiveUtils.watch(
        () => view.interacting,
        (isInteracting) => {
          if (isInteracting) {
            activeView = view;
            sync(activeView);
          }
        }
      );

      reactiveUtils.watch(
        () => view.viewpoint,
        () => sync(view),
        { initial: false }
      );
    });
  }

  public toggleViewContainer(el: ElementRef, viewType: EsriViewType): void {
    store.views[viewType].view.update((view) => {
      if (view) {
        view.container = el?.nativeElement;
      }
      return view;
    });
  }

  public setBasemap(basemap: Basemap): void {
    const map = store.map();
    if (!map || map.basemap?.id === basemap?.id) return;
    map.basemap = basemap;
  }
}
